import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import analytics from "../../services/analytics";

const BRAND_COLORS = ["#FFE100", "#3b82f6", "#22d3ee", "#c084fc", "#60a5fa"];
const SUPPORT_EMAIL = "support@ultimatewebscraper.com";

const fireConfetti = () => {
  confetti({
    particleCount: 60,
    spread: 70,
    origin: { y: 0.6, x: 0.5 },
    colors: BRAND_COLORS,
    gravity: 1.2,
    scalar: 0.9,
    ticks: 150,
    disableForReducedMotion: true,
  });

  setTimeout(() => {
    confetti({
      particleCount: 35,
      spread: 100,
      origin: { y: 0.55, x: 0.5 },
      colors: BRAND_COLORS,
      gravity: 1.4,
      scalar: 0.7,
      ticks: 120,
      disableForReducedMotion: true,
    });
  }, 150);
};

const CheckItem = ({ children }) => (
  <li className="flex items-start gap-3 text-left">
    <svg
      viewBox="0 0 20 20"
      className="flex-shrink-0 w-4 h-4 mt-[3px]"
      fill="none"
      stroke="url(#rowIconGradient)"
      strokeWidth={2.25}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 10.5l4 4 8-9" />
    </svg>
    <span className="text-sm text-gray-700 dark:text-zinc-300 leading-relaxed">{children}</span>
  </li>
);

const CheckoutSuccess = ({ plan, interval, source }) => {
  const navigate = useNavigate();
  const confettiFired = useRef(false);

  useEffect(() => {
    if (!confettiFired.current) {
      confettiFired.current = true;
      setTimeout(fireConfetti, 300);
      analytics.trackCheckoutCompleted({
        plan,
        interval,
        source,
        flow: "wizard",
        type: "subscription",
      });
    }
  }, [plan, interval, source]);

  return (
    <div className="w-full max-w-lg mx-auto">
      {/* Shared gradient for row icons */}
      <svg width="0" height="0" className="absolute">
        <defs>
          <linearGradient id="rowIconGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="50%" stopColor="#22d3ee" />
            <stop offset="100%" stopColor="#c084fc" />
          </linearGradient>
        </defs>
      </svg>

      <div className="bg-white/80 dark:bg-zinc-900/80 backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-zinc-800 overflow-hidden">
        {/* Top accent gradient */}
        <div className="h-1.5 bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500" />

        <div className="px-8 pt-10 pb-6 text-center">
          {/* Brand logo with checkmark */}
          <div className="relative mx-auto w-20 h-20 mb-6">
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 opacity-20 dark:opacity-30 blur-xl" />
            <div className="relative w-20 h-20 rounded-2xl p-[2px] bg-gradient-to-br from-blue-400 via-cyan-400 to-purple-400 shadow-lg shadow-cyan-500/20">
              <div className="w-full h-full bg-white dark:bg-zinc-900 rounded-[14px] p-2.5 flex items-center justify-center">
                <img src="/logo-128.png" alt="Ultimate Web Scraper" className="w-full h-full" />
              </div>
            </div>
            {/* Animated checkmark badge */}
            <div className="absolute -bottom-1.5 -right-1.5 w-8 h-8 rounded-full bg-white dark:bg-zinc-900 shadow-lg flex items-center justify-center ring-2 ring-white dark:ring-zinc-900">
              <svg viewBox="0 0 36 36" className="w-full h-full">
                <style>{`
                  @keyframes circleDraw { from { stroke-dashoffset: 88; } to { stroke-dashoffset: 0; } }
                  @keyframes checkDraw { from { stroke-dashoffset: 24; } to { stroke-dashoffset: 0; } }
                `}</style>
                <circle
                  cx="18" cy="18" r="14"
                  fill="none"
                  stroke="url(#successCheckGradient)"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  style={{ strokeDasharray: 88, strokeDashoffset: 88, animation: "circleDraw 0.5s ease-out 0.4s forwards" }}
                />
                <path
                  d="M11 19l4 4 9-9"
                  fill="none"
                  stroke="url(#successCheckGradient)"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ strokeDasharray: 24, strokeDashoffset: 24, animation: "checkDraw 0.35s ease-out 0.85s forwards" }}
                />
                <defs>
                  <linearGradient id="successCheckGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#3b82f6" />
                    <stop offset="50%" stopColor="#22d3ee" />
                    <stop offset="100%" stopColor="#c084fc" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
          </div>

          {/* Heading */}
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
            You're all set!
          </h2>
          <p className="text-sm text-gray-500 dark:text-zinc-400 mt-2">
            Your subscription is now active.
          </p>

          {/* How to activate Extension PRO */}
          <div className="mt-8 text-left rounded-xl border border-gray-200 dark:border-zinc-800 bg-white/50 dark:bg-zinc-900/40 px-5 py-4">
            <p className="text-[11px] font-semibold text-gray-400 dark:text-zinc-500 uppercase tracking-wider mb-2.5">
              How to activate Extension PRO
            </p>
            <ol className="space-y-1.5">
              <li className="flex gap-2.5 text-sm text-gray-700 dark:text-zinc-300 leading-relaxed">
                <span className="flex-shrink-0 text-gray-400 dark:text-zinc-500 font-medium tabular-nums">1.</span>
                <span>
                  Open the extension →{" "}
                  <span className="font-medium text-gray-900 dark:text-white">Settings</span>
                  {" "}<span aria-hidden="true">⚙️</span>
                </span>
              </li>
              <li className="flex gap-2.5 text-sm text-gray-700 dark:text-zinc-300 leading-relaxed">
                <span className="flex-shrink-0 text-gray-400 dark:text-zinc-500 font-medium tabular-nums">2.</span>
                <span>Sign in to your account</span>
              </li>
            </ol>
          </div>

          {/* Primary CTA */}
          <div className="mt-8">
            <button
              onClick={() => navigate("/dashboard")}
              className="w-full py-3 rounded-xl font-bold text-sm bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 text-white hover:from-blue-600 hover:via-cyan-600 hover:to-purple-600 transition-all duration-200 shadow-md shadow-cyan-500/20 hover:shadow-lg hover:shadow-cyan-500/30 hover:scale-[1.02] active:scale-[0.98] transform"
            >
              Go to Dashboard
            </button>
          </div>

          {/* Footer */}
          <p className="text-xs text-gray-400 dark:text-zinc-500 mt-6">
            Need help?{" "}
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="text-gray-500 dark:text-zinc-400 hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors"
            >
              {SUPPORT_EMAIL}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccess;
